import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

const getQrCodeIdFromUrl = (text) => {
  if (!text || typeof text !== "string") return "";
  const raw = text.trim();
  if (!raw) return "";
  try {
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      const url = new URL(raw);
      const pathParts = url.pathname.split("/").filter(Boolean);
      const locketIndex = pathParts.findIndex((p) => p === "locket");
      if (locketIndex !== -1 && pathParts.length > locketIndex + 1) {
        return pathParts[locketIndex + 1].toUpperCase();
      }
      const qrIndex = pathParts.findIndex((p) => p === "qr");
      if (qrIndex !== -1 && pathParts.length > qrIndex + 1) {
        return pathParts[qrIndex + 1].toUpperCase();
      }
      if (pathParts.length > 0) {
        return pathParts[pathParts.length - 1].toUpperCase();
      }
    }
  } catch (_) {}
  return raw.toUpperCase();
};

const QR_READER_ID = "qr-scanner-reader";

const cleanupScannerDom = () => {
  const el = document.getElementById(QR_READER_ID);
  if (el) {
    const videos = el.querySelectorAll("video");
    videos.forEach((v) => {
      try {
        if (v.srcObject) {
          v.srcObject.getTracks().forEach((t) => t.stop());
          v.srcObject = null;
        }
      } catch (_) {}
    });
    el.innerHTML = "";
  }
};

const QRScanner = ({ onScan, onClose, isOpen }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const scannerRef = useRef(null);
  const startScanningRef = useRef(null);
  const stopScanningRef = useRef(null);
  const startPromiseRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [manualInput, setManualInput] = useState("");
  const [activeTab, setActiveTab] = useState("scan");

  // Login tab state
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const stopScanning = useCallback(async () => {
    if (startPromiseRef.current) {
      try { await startPromiseRef.current; } catch (_) {}
      startPromiseRef.current = null;
    }
    if (scannerRef.current) {
      const scanner = scannerRef.current;
      scannerRef.current = null;
      const el = document.getElementById(QR_READER_ID);
      const video = el?.querySelector("video");
      if (video) { video.onabort = null; video.onerror = null; }
      try { await scanner.stop(); } catch (_) {}
      try { scanner.clear(); } catch (_) {}
    }
    setIsScanning(false);
  }, []);

  const startScanning = useCallback(async () => {
    const element = document.getElementById(QR_READER_ID);
    if (!element) return;

    try {
      setError(null);
      setIsScanning(true);

      const isSecureContext =
        window.location.protocol === "https:" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isSecureContext) {
        setError("Camera requires HTTPS. Use manual entry instead.");
        setIsScanning(false);
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setIsScanning(false);
        return;
      }

      const scanner = new Html5Qrcode(QR_READER_ID, { verbose: false });
      scannerRef.current = scanner;

      const boxSize = Math.min(Math.floor(window.innerWidth * 0.6), 250);
      const config = {
        fps: 15,
        qrbox: { width: boxSize, height: boxSize },
        aspectRatio: 1,
        videoConstraints: { facingMode: "environment" },
      };

      const startPromise = scanner.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          const qrCodeId = getQrCodeIdFromUrl(decodedText);
          if (qrCodeId) {
            stopScanning();
            onScan(qrCodeId);
            onClose();
          }
        },
        () => {}
      );
      startPromiseRef.current = startPromise;
      try {
        await startPromise;
      } finally {
        startPromiseRef.current = null;
      }
    } catch (err) {
      console.error("QR scanner error:", err);
      scannerRef.current = null;
      cleanupScannerDom();

      if (err.name === "NotAllowedError") {
        setIsScanning(false);
        return;
      }

      let errorMessage = "Failed to start camera";
      if (err.name === "NotFoundError") {
        errorMessage = "No camera found on this device.";
      } else if (err.name === "NotSupportedError") {
        errorMessage = "Camera is not supported on this device.";
      } else if (err.message && err.message.includes("HTTPS")) {
        errorMessage = "Camera access requires HTTPS.";
      } else {
        errorMessage = err.message || "Failed to start camera";
      }
      setError(errorMessage);
      setIsScanning(false);
    }
  }, [onScan, onClose, stopScanning]);

  startScanningRef.current = startScanning;
  stopScanningRef.current = stopScanning;

  useEffect(() => {
    if (!isOpen || activeTab !== "scan") return;

    const timer = setTimeout(() => {
      startScanningRef.current?.();
    }, 150);

    return () => {
      clearTimeout(timer);
      stopScanningRef.current?.();
    };
  }, [isOpen, activeTab]);

  const handleClose = () => {
    stopScanning();
    setManualInput("");
    setActiveTab("scan");
    setLoginIdentifier("");
    setLoginPassword("");
    setLoginError(null);
    onClose();
  };

  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      const qrCodeId = getQrCodeIdFromUrl(manualInput.trim());
      if (qrCodeId) {
        stopScanning();
        navigate(`/${qrCodeId}/public`);
        handleClose();
      }
    }
  };

  const handleLogin = async () => {
    if (!loginIdentifier.trim() || !loginPassword.trim()) return;
    setLoginError(null);
    setLoginLoading(true);
    try {
      const isPhone = /^[6-9]\d{9}$/.test(loginIdentifier.trim());
      let res;
      if (isPhone) {
        res = await api.post("/qr/login", { phone: loginIdentifier.trim(), password: loginPassword });
      } else {
        const id = loginIdentifier.trim().toUpperCase();
        res = await api.post(`/qr/${id}/login`, { password: loginPassword });
      }
      if (!res.data?.token) {
        setLoginError("Login succeeded but no token received.");
        return;
      }
      login(res.data.token);
      const locketId = res.data.customer?.locketId || loginIdentifier.trim().toUpperCase();
      handleClose();
      navigate(`/${locketId}/info`);
    } catch (err) {
      if (!err.response) {
        setLoginError("Unable to reach server. Check your connection.");
      } else if (err.response.status === 429) {
        setLoginError("Too many attempts. Please wait.");
      } else {
        setLoginError(err.response?.data?.message || err.response?.data?.error || "Login failed.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleTabSwitch = (tab) => {
    if (tab === "login" && activeTab === "scan") {
      stopScanning();
    }
    setActiveTab(tab);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "90vh",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>
            {activeTab === "scan" ? "Scan or Enter QR Code" : "Owner Login"}
          </h3>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: "transparent",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              padding: "4px",
              color: "#64748b",
              lineHeight: "1",
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
          <button
            onClick={() => handleTabSwitch("scan")}
            style={{
              flex: 1,
              padding: "12px",
              border: "none",
              backgroundColor: "transparent",
              borderBottom: activeTab === "scan" ? "2px solid #dc2626" : "2px solid transparent",
              color: activeTab === "scan" ? "#dc2626" : "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Scan
          </button>
          <button
            onClick={() => handleTabSwitch("login")}
            style={{
              flex: 1,
              padding: "12px",
              border: "none",
              backgroundColor: "transparent",
              borderBottom: activeTab === "login" ? "2px solid #dc2626" : "2px solid transparent",
              color: activeTab === "login" ? "#dc2626" : "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Login
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px", maxHeight: "calc(90vh - 140px)", overflowY: "auto" }}>
          {/* Scan Tab */}
          <div style={{ display: activeTab === "scan" ? "block" : "none" }}>
            {error ? (
              <div style={{ color: "#dc2626", textAlign: "center", padding: "12px 0" }}>
                <p style={{ marginBottom: "12px", fontSize: "14px" }}>{error}</p>
                <button
                  onClick={() => { setError(null); startScanning(); }}
                  style={{ backgroundColor: "#dc2626", color: "white", border: "none", padding: "8px 16px", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div style={{ textAlign: "center" }}>
                <div style={{ padding: "8px 0 6px", color: "#64748b", fontSize: "12px" }}>
                  {isScanning ? "Camera active — point at QR code" : "Starting camera..."}
                </div>
              </div>
            )}
            <div
              id={QR_READER_ID}
              style={{ borderRadius: "8px", overflow: "hidden", backgroundColor: "#000", display: error ? "none" : "block" }}
            />

            {/* Manual entry below camera */}
            <div style={{ marginTop: "16px", borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: "600", color: "#334155" }}>
                Or enter QR Code ID manually
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="e.g. ABC123XY"
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "14px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#dc2626")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                  onKeyPress={(e) => { if (e.key === "Enter") handleManualSubmit(); }}
                />
                <button
                  onClick={handleManualSubmit}
                  disabled={!manualInput.trim()}
                  style={{
                    backgroundColor: manualInput.trim() ? "#dc2626" : "#cbd5e1",
                    color: "white",
                    border: "none",
                    padding: "10px 16px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: manualInput.trim() ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                  }}
                >
                  Go
                </button>
              </div>
            </div>
          </div>

          {/* Login Tab */}
          <div style={{ display: activeTab === "login" ? "block" : "none" }}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: "600", color: "#334155" }}>
                QR Code ID or Mobile Number
              </label>
              <input
                type="text"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                placeholder="e.g. ABC123 or 9876543210"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#dc2626")}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: "600", color: "#334155" }}>
                Password
              </label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#dc2626")}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                onKeyPress={(e) => { if (e.key === "Enter") handleLogin(); }}
              />
            </div>
            {loginError && (
              <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", padding: "10px 12px", marginBottom: "16px" }}>
                <p style={{ margin: 0, color: "#dc2626", fontSize: "13px", fontWeight: "500" }}>{loginError}</p>
              </div>
            )}
            <button
              onClick={handleLogin}
              disabled={loginLoading || !loginIdentifier.trim() || !loginPassword.trim()}
              style={{
                width: "100%",
                backgroundColor: loginLoading ? "#94a3b8" : "#dc2626",
                color: "white",
                border: "none",
                padding: "12px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: loginLoading ? "not-allowed" : "pointer",
              }}
            >
              {loginLoading ? "Logging in..." : "Log In"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
